import React, { useState, useEffect } from "react";

import { useTypedSelector } from "../../redux/reducers/RootReducer";
import {
  getModuleState,
  selectDetailModuleStateByType,
} from "../../redux/reducers/ModuleStateReducer";

import OverviewStatus, { EOverviewModuleStatus } from "./OverviewStatus";
import ProgressStatusBar from "./ProgressStatusBar/ProgressStatusBar";

import { Module } from "../../Model/Module";
import { ESuiteMode, ELimsConnectionStatus } from "../../Model/ModuleState";
import { EModuleType } from "../../Model/EModuleType";
import { EImagingMode } from "../../Model/EImagingMode";

interface SuiteStatusProps {
  module: Module;
}

const getModuleStatus = (suiteMode?: ESuiteMode) => {
  switch (suiteMode) {
    case ESuiteMode.Normal:
      return EOverviewModuleStatus.Idle;
    case ESuiteMode.Testing:
      return EOverviewModuleStatus.Busy;
    default:
      return EOverviewModuleStatus.Error;
  }
};

export default function SuiteStatus(props: SuiteStatusProps) {
  const { module } = props;

  const { suite: suiteState } = useTypedSelector((state) =>
    getModuleState(state, module.identifier)
  );
  const imagerState = useTypedSelector((state) =>
    selectDetailModuleStateByType(state, EModuleType.IMAGER)
  );

  const [limsConnectionText, setLimsConnectionText] = useState(
    ELimsConnectionStatus.Unknown.toString()
  );

  useEffect(() => {
    setLimsConnectionText(
      suiteState?.limsConnectionStatus != null
        ? ELimsConnectionStatus[
            suiteState.limsConnectionStatus.status
          ].toString()
        : ELimsConnectionStatus[ELimsConnectionStatus.Unknown].toString()
    );
  }, [suiteState]);

  // DISK SPACE
  const freeSpace =
    imagerState?.imager?.resourceStatus?.driveSpace?.availableFreeSpaceInByte ??
    0;
  const totalSpace =
    imagerState?.imager?.resourceStatus?.driveSpace?.totalDriveSpaceInByte ?? 0;
  const freeSpaceInGb = `${(freeSpace / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  const totalSpaceInGb = `${(totalSpace / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  const occupiedSpaceinPercent =
    ((totalSpace - freeSpace) * 100.0) / totalSpace;
  const diskSpaceText =
    freeSpace != null
      ? `${freeSpaceInGb} free of ${totalSpaceInGb}`
      : "Unknown";

  // MEMORY
  const freeMemory =
    imagerState?.imager?.resourceStatus?.memory?.availableFreeMemoryInByte ?? 0;
  const totalMemory =
    imagerState?.imager?.resourceStatus?.memory?.totalMemoryInByte ?? 0;
  const freeMemoryInMb = `${(freeMemory / (1024 * 1024)).toFixed(2)} MB`;
  const totalMemoryInMb = `${(totalMemory / (1024 * 1024)).toFixed(2)} MB`;
  const occupiedMemoryinPercent =
    ((totalMemory - freeMemory) * 100.0) / totalMemory;
  const memorySpaceText =
    freeMemory != null
      ? `${freeMemoryInMb} free of ${totalMemoryInMb}`
      : "Unknown";

  const moduleStatus = getModuleStatus(suiteState?.suiteMode);
  const imagingModeText = suiteState
    ? EImagingMode[suiteState.imagingMode]
    : "Unknown";

  const statusses = {
    "Database [Lims]": limsConnectionText,
    "Imaging Mode": imagingModeText,
    Storage: diskSpaceText,
    hide_storage: (
      <ProgressStatusBar
        value={occupiedSpaceinPercent}
        style={{ width: "80%" }}
      />
    ),
    Memory: memorySpaceText,
    hide_memory: (
      <ProgressStatusBar
        value={occupiedMemoryinPercent}
        style={{ width: "80%" }}
      />
    ),
  };

  return (
    <OverviewStatus
      name="SUITE"
      statusses={statusses}
      moduleStatus={moduleStatus}
      moduleType={EModuleType.SUITE}
    />
  );
}
